/**
 * tool-ad-slot.js — hydrates <div.tool-ad-slot[data-ad-slot-provider]>
 * placeholders rendered by components/tool-ad-slot.blade.php.
 *
 * Single runtime path for both providers (yandex + adsense). Provider
 * SDK loads ON DEMAND — adsbygoogle.js / context.js only enter the page
 * when at least one slot exists. There is NO global <script src=
 * "adsbygoogle.js?client=...">: that path enables Auto Ads (Anchor +
 * Vignette overlays at z-index 2147483647) which was the root cause of
 * the 2026-05-12 invalid-traffic limitation.
 *
 * Layered defense:
 *   1. tmb_no_ads=1 cookie OR body.dataset.hideAds === '1' → bail
 *      (admin + paid no-ads). Cookie covers the case where Cloudflare
 *      serves a cached anonymous HTML to an admin/paid user — the
 *      <body> attribute lives in cached HTML and is lost on that path,
 *      but the cookie survives in the browser regardless. Cookie set
 *      by app/Http/Middleware/AdGateCookie.php on every server render.
 *   2. No slot DOM → SDK never loads (saves ~120 KB on home / blog).
 *   3. Mid slot lazy-hydrates via IntersectionObserver (rootMargin 500px) —
 *      a user who never scrolls to mid never triggers an ad_failed and
 *      never burns the SDK request quota for a slot they wouldn't see.
 *      Top slot is always above-the-fold, hydrates eagerly.
 *   4. 6s collapse-on-fail timeout with structured reason. Reasons:
 *        - sdk_blocked        → script tag .onerror (adblock / DNS / CSP)
 *        - timeout_no_sdk     → 6s elapsed, SDK script never loaded
 *        - timeout_after_sdk  → 6s elapsed, SDK loaded but render callback
 *                               never fired (Yandex/AdSense slow / no fill)
 *        - yandex_<CODE>      → onError from Ya.Context (CONTAINER_IS_HIDDEN,
 *                               TOO_SMALL_CONTAINER, BLOCK_NOT_FOUND...)
 *        - unfilled           → AdSense data-ad-status=unfilled (no advertiser)
 *        - zero_width         → the slot has no width when it is its turn to ask
 *                               (a blocker's cosmetic rule, a hidden container):
 *                               nothing is requested, see hydrateAdsense
 *   5. ad_impression / ad_failed wired to window.__ae for both providers,
 *      single observer per slot. ad_impression carries render_ms so we
 *      can tune TIMEOUT_MS based on real p95 of successful loads.
 */
(function () {
    'use strict';

    var TIMEOUT_MS = 6000;

    // Per-provider load state, shared across all slots of that provider on
    // the page. 'unknown' until the SDK script tag's onload or onerror
    // fires — slots wait this resolves before classifying their own
    // timeout (so a slow connection doesn't get mislabelled as adblock).
    var sdkState = { yandex: 'unknown', adsense: 'unknown' }; // unknown | loaded | failed
    // What runs the moment a provider's SDK has loaded (see whenSdk).
    var sdkWaiters = { yandex: [], adsense: [] };
    function whenSdk(provider, fn) {
        if (sdkState[provider] === 'loaded') { fn(); return; }
        sdkWaiters[provider].push(fn);
    }

    // «Просить ли рекламу у этого клиента» решает ОДИН примитив —
    // window.__adTrafficSkip, объявленный в layouts/app.blade.php (там же
    // замер, на котором стоит его признак машины). Здесь копии условий нет
    // намеренно: до 18.09.2026 та же регулярка жила в трёх местах, и ферма
    // headless-браузеров прошла все три. Вторая копия — способ разъехаться
    // молча, а цена расхождения на этой поверхности — рекламный аккаунт.
    // robots.txt по-прежнему Allow:s GPTBot/ClaudeBot/PerplexityBot — они
    // индексируют и цитируют нас, просто не получают рекламу.

    var __ae = function (type, payload) {
        if (window.__ae) { try { window.__ae(type, payload); } catch (_) {} }
    };

    function shouldHideAds() {
        if (document.body && document.body.dataset.hideAds === '1') return true;
        // Defense against CF/PageCache-poisoning: <body data-hide-ads> can
        // be missing if an anonymous-cached HTML reaches an admin/paid user.
        if (document.cookie.indexOf('tmb_no_ads=1') !== -1) return true;
        // Telegram Mini App webview (tmb_tg set by the /tg entry page):
        // AdSense program policy forbids serving inside webviews — rendering
        // there is the invalid-traffic class that caused the 2026-05-12
        // limitation; РСЯ is the same risk. Inside Telegram the revenue
        // surface is the Adsgram interstitial (telegram-adapter.js) instead.
        // Checked here — NOT in the adapter — so ad hygiene holds even if
        // the adapter script fails to load.
        if (document.cookie.indexOf('tmb_tg=1') !== -1) return true;
        // Framed context (Telegram Web runs Mini Apps in an iframe on
        // web.telegram.org, where SameSite=Lax cookies never attach, so the
        // tmb_tg check above is blind there; frame-ancestors allows that one
        // origin). Serving AdSense/РСЯ inside someone else's frame is the
        // same policy class — suppress whenever we are not the top window.
        // A cross-origin parent makes reading window.top throw: also framed.
        try { return window.top !== window.self; } catch (_) { return true; }
    }

    function collapseOnFail(slotEl, reason) {
        if (!slotEl || slotEl.classList.contains('is-loaded') || slotEl.classList.contains('is-failed')) return;
        // Slot removed from the DOM (focus-mode removePending, admin path) —
        // a late SDK callback / 6s timeout on a detached node is not an ad
        // failure, just the tail of an intentionally cancelled slot.
        if (!slotEl.isConnected) return;
        slotEl.classList.add('is-failed');
        // Drop the provider container. adsbygoogle.js sizes the responsive
        // <ins> (~300px for a 900px column) when it processes the push,
        // BEFORE the auction resolves — on unfilled / timeout_after_sdk that
        // blank reservation stays as a sibling under the fallback, so the
        // page showed the trending grid AND ~300px of dead space below it
        // (reported 2026-06-12 on /en/reverse; intl-only — the Yandex
        // container is an unstyled 0-height div on failure). Removal rather
        // than display:none also stops a late fill from landing in a slot
        // we've written off — the hidden-fill ghost-impression class
        // removePending() guards against.
        slotEl.querySelectorAll('ins.adsbygoogle, [id^="yandex_rtb_"]').forEach(function (el) { el.remove(); });
        __ae('ad_failed', {
            provider:  slotEl.dataset.adSlotProvider || 'unknown',
            placement: slotEl.dataset.adSlotPos || 'unknown',
            block:     slotEl.dataset.adSlotId || '',
            reason:    reason || 'unknown'
        });
    }

    function markLoaded(slotEl, startedAt) {
        if (!slotEl || slotEl.classList.contains('is-loaded')) return;
        slotEl.classList.add('is-loaded');
        __ae('ad_impression', {
            provider:  slotEl.dataset.adSlotProvider || 'unknown',
            placement: slotEl.dataset.adSlotPos || 'unknown',
            block:     slotEl.dataset.adSlotId || '',
            render_ms: startedAt ? (Date.now() - startedAt) : null
        });
    }

    // SDK script tag — separate from `injectScriptOnce` so we can attach
    // load/error handlers that classify the SDK state for downstream
    // timeout decisions. Idempotent: subsequent calls are no-ops.
    function loadSdk(provider, src, crossOrigin) {
        if (sdkState[provider] === 'loaded' || sdkState[provider] === 'failed') return;
        var id = provider === 'yandex' ? 'ya-context-js' : 'adsbygoogle-js';
        var onLoad = function () {
            sdkState[provider] = 'loaded';
            var run = sdkWaiters[provider]; sdkWaiters[provider] = [];
            run.forEach(function (fn) { try { fn(); } catch (_) {} });
        };
        // The tag is already in the page: this module put it there for an earlier
        // slot and it is still on its way — its own load handler will run the
        // waiters. It used to be read as «loaded» on sight, which queued the next
        // slot's push into the array again.
        if (document.getElementById(id)) {
            if (provider === 'adsense' && window.adsbygoogle && window.adsbygoogle.loaded) onLoad();
            return;
        }
        var s = document.createElement('script');
        s.id = id;
        s.src = src;
        s.async = true;
        if (crossOrigin) s.crossOrigin = crossOrigin;
        s.addEventListener('load', onLoad);
        s.addEventListener('error', function () {
            sdkState[provider] = 'failed';
            // Eager-fail TOP slots so the fallback paints immediately
            // instead of waiting out the 6s timeout. Mid slots are still
            // pending in IntersectionObserver — they check sdkState when
            // they hydrate, so we don't record an ad_failed event for an
            // ad the user might never scroll to.
            document.querySelectorAll('.tool-ad-slot[data-ad-slot-provider="' + provider + '"][data-ad-slot-pos="top"]')
                .forEach(function (el) { collapseOnFail(el, 'sdk_blocked'); });
        });
        document.head.appendChild(s);
    }

    function hydrateYandex(slotEl) {
        var blockId = slotEl.dataset.adSlotId;
        var renderId = 'yandex_rtb_' + blockId;
        var child = document.createElement('div');
        child.id = renderId;
        slotEl.appendChild(child);

        var startedAt = Date.now();

        window.yaContextCb = window.yaContextCb || [];
        window.yaContextCb.push(function () {
            try {
                window.Ya.Context.AdvManager.render({
                    blockId: blockId,
                    renderTo: renderId,
                    onRender: function () { markLoaded(slotEl, startedAt); },
                    onError: function (data) {
                        var r = (data && (data.code || data.reason)) || 'unknown';
                        collapseOnFail(slotEl, 'yandex_' + String(r).slice(0, 32));
                    }
                });
            } catch (e) {
                collapseOnFail(slotEl, 'yandex_render_throw');
            }
        });

        loadSdk('yandex', 'https://yandex.ru/ads/system/context.js');
        scheduleTimeout(slotEl, 'yandex');
    }

    function hydrateAdsense(slotEl) {
        var client = slotEl.dataset.adSlotClient;
        if (!client) { collapseOnFail(slotEl, 'no_client'); return; }

        var slotId = slotEl.dataset.adSlotId;
        var ins = document.createElement('ins');
        ins.className = 'adsbygoogle';
        ins.style.cssText = 'display:block;width:100%';
        ins.setAttribute('data-ad-client', client);
        ins.setAttribute('data-ad-slot', slotId);
        ins.setAttribute('data-ad-format', 'auto');
        ins.setAttribute('data-full-width-responsive', 'true');
        slotEl.appendChild(ins);

        var startedAt = Date.now();

        // AdSense sets data-ad-status="filled"|"unfilled" once the SDK
        // decides. One observer per slot — the global MutationObserver in
        // layouts/app.blade.php was removed when this component became the
        // single ad-rendering path.
        var mo = new MutationObserver(function () {
            var s = ins.getAttribute('data-ad-status');
            if (s === 'filled')   { mo.disconnect(); markLoaded(slotEl, startedAt); }
            if (s === 'unfilled') { mo.disconnect(); collapseOnFail(slotEl, 'unfilled'); }
        });
        try { mo.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] }); }
        catch (e) {}

        // A slot with no width is not asked for: AdSense answers it with
        // «TagError: adsbygoogle.push() error: No slot size for availableWidth=0».
        // The width is zero when a content blocker hides ins.adsbygoogle with a
        // cosmetic rule while letting the SDK through, when the slot sits in a
        // hidden container, or when a privacy browser hides "empty ad containers"
        // a moment after they appear.
        //
        // And the push is made only once the SDK HAS LOADED. It used to be queued
        // into the array before that — the documented snippet — and the SDK then
        // processed the queue inside its own load, where the error is thrown into
        // window.onerror and no try/catch of ours can reach it: the slot sat out
        // its 6 s as «timeout_after_sdk», and the page logged the SDK's exception
        // as its own (intl, 3 days to 14.10.2026: 236 sessions, every browser
        // family; 61 of 90 sessions of the DuckDuckGo cohort). A push into a
        // loaded SDK is processed synchronously — the width is the width of that
        // instant, and a throw is caught here.
        // Lab with the real SDK (tests/playwright/ad-slot-zero-width-is-not-asked.mjs):
        // hidden by a rule, hidden by a parent, hidden 30 ms after insertion — one
        // uncaught TagError each before, none after; a visible slot is asked as before.
        if (!(ins.offsetWidth > 0)) { mo.disconnect(); collapseOnFail(slotEl, 'zero_width'); return; }

        // Minors → non-personalised ads (set server-side via window.__npa).
        try { if (window.__npa) (window.adsbygoogle = window.adsbygoogle || []).requestNonPersonalizedAds = 1; } catch (e) {}
        whenSdk('adsense', function () {
            if (!slotEl.isConnected || !ins.isConnected) return;
            if (slotEl.classList.contains('is-failed') || slotEl.classList.contains('is-loaded')) return;
            if (!(ins.offsetWidth > 0)) { mo.disconnect(); collapseOnFail(slotEl, 'zero_width'); return; }
            try { (window.adsbygoogle = window.adsbygoogle || []).push({}); }
            catch (e) { mo.disconnect(); collapseOnFail(slotEl, 'push_throw'); }
        });

        // ⚠️ Здесь до 18.09.2026 стояло: «загрузка adsbygoogle.js отсюда, а не
        // из head, — СТРУКТУРНАЯ защита от Auto Ads; без data-ad-client на теге
        // скрипта Google не может внедрить Anchor/Vignette». Это НЕВЕРНО и
        // давало ложное спокойствие: у Google ОДИН тег на оба случая («Auto ads
        // now works through any AdSense ad unit code» —
        // support.google.com/adsense/answer/9274634), а `?client=` в URL это и
        // есть объявление клиента, отсутствием которого комментарий
        // оправдывался. Загрузка по требованию экономит ~120 КБ на страницах
        // без слотов и сужает окно, но Auto Ads она НЕ запрещает.
        //
        // Настоящий рычаг один — тумблер в консоли AdSense (Сайты →
        // tembrica.com → Автоматические объявления → ВЫКЛ). Из кода его не
        // прочитать, поэтому рядом стоит ДАТЧИК: watchAutoAds() ниже шлёт
        // телеметрию, если Anchor/Vignette всё-таки появились. Утверждение,
        // которое нечем перепроверить, — обещание, а не факт; ограничение
        // 12.05.2026 по невалидному трафику началось именно с этих оверлеев.
        loadSdk(
            'adsense',
            'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=' + encodeURIComponent(client),
            'anonymous'
        );
        scheduleTimeout(slotEl, 'adsense');
    }

    function scheduleTimeout(slotEl, provider) {
        setTimeout(function () {
            if (slotEl.classList.contains('is-loaded') || slotEl.classList.contains('is-failed')) return;
            var reason = sdkState[provider] === 'loaded' ? 'timeout_after_sdk' : 'timeout_no_sdk';
            collapseOnFail(slotEl, reason);
        }, TIMEOUT_MS);
    }

    // Ждать видимости умеет тот же примитив, что судит о трафике
    // (window.__adTrafficSkip.whenVisible в layouts/app.blade.php) — фоновая
    // вкладка и пререндер там объяснены одним абзацем, потому что дают одно и
    // то же состояние. Своей копии здесь нет по той же причине, что и у
    // признака машины: расходятся молча, платит аккаунт.
    var __hiddenReported = false;
    function hydrate(slotEl) {
        if (document.visibilityState === 'hidden' && !__hiddenReported) {
            __hiddenReported = true;
            __ae('ad_deferred_hidden', {});
        }
        window.__adTrafficSkip.whenVisible(function () { hydrateNow(slotEl); });
    }

    function hydrateNow(slotEl) {
        if (!slotEl.isConnected) return;
        var provider = slotEl.dataset.adSlotProvider;
        // SDK already proven unreachable — no point queueing into
        // yaContextCb or pushing into adsbygoogle. This is the lazy-mid
        // path's normal entry when the user finally scrolls in.
        if (sdkState[provider] === 'failed') { collapseOnFail(slotEl, 'sdk_blocked'); return; }
        if (provider === 'yandex')  hydrateYandex(slotEl);
        else if (provider === 'adsense') hydrateAdsense(slotEl);
        else collapseOnFail(slotEl, 'unknown_provider');
    }

    // Mid slot defers hydration until close to viewport — no point
    // burning an SDK request on an ad the user might never see.
    // Top slot is always above the fold, hydrates eagerly.
    function maybeLazyHydrate(slotEl) {
        var pos = slotEl.dataset.adSlotPos;
        if (pos !== 'mid' || !('IntersectionObserver' in window)) {
            hydrate(slotEl);
            return;
        }
        var io = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting) {
                io.disconnect();
                hydrate(slotEl);
            }
        }, { rootMargin: '500px 0px' });
        io.observe(slotEl);
    }

    function init() {
        var slots = document.querySelectorAll('.tool-ad-slot[data-ad-slot-provider]');
        if (!slots.length) return;

        function dropAll(reason) {
            document.querySelectorAll('.tool-ad-slot[data-ad-slot-provider]')
                .forEach(function (slotEl) { slotEl.remove(); });
            if (reason) __ae('ad_skipped', { reason: reason });
        }

        // Гейт трафика не доехал — старый HTML из кэша, срезанный CSP инлайн,
        // урезанная сборка. Локальной копии проверок здесь нет по построению
        // (см. комментарий у объявления __ae выше), поэтому единственный
        // честный ответ — не просить. Ошибка в эту сторону стоит показов,
        // ошибка в обратную — аккаунта; цена несимметрична.
        if (typeof window.__adTrafficSkip !== 'function') {
            dropAll('gate_missing');
            return;
        }

        window.__adTrafficSkip(function (botReason) {
            if (botReason) { dropAll(botReason); return; }
            initAllowed();
        });
    }

    // Датчик Auto Ads. Тумблер «Автоматические объявления» живёт в консоли
    // AdSense, а не у нас (см. длинный комментарий в hydrateAdsense), значит
    // код может лишь ЗАМЕТИТЬ, что оверлеи появились. Ловим два вида: Anchor
    // (он же ins.adsbygoogle-noablate) и любой ins.adsbygoogle, который создали
    // не мы, плюс Vignette — та объявляет себя хэшем #google_vignette.
    // Без датчика «Auto Ads у нас выключены» — непроверяемое утверждение, а
    // именно эти оверлеи стоили ограничения 12.05.2026.
    function watchAutoAds() {
        if (!window.MutationObserver) return;
        var seen = {};
        function report(kind, detail) {
            if (seen[kind]) return;
            seen[kind] = 1;
            __ae('ad_auto_injected', { kind: kind, detail: String(detail || '').slice(0, 80) });
        }
        function ours(el) {
            if (!el.closest) return false;
            return !!(el.closest('.tool-ad-slot') || el.closest('#asStickyWrap') || el.closest('#rsyaStickyWrap'));
        }
        function inspect(el) {
            // Seen after it was taken out again: our own ins of a slot that was not
            // asked (zero width) is removed in the same tick it was added, and a
            // detached node has no .tool-ad-slot ancestor to be recognised by.
            if (!el.isConnected) return;
            if (ours(el)) return;
            report(el.className && el.className.indexOf('noablate') !== -1 ? 'anchor' : 'foreign_ins',
                   (el.className || '') + '|' + (el.getAttribute('data-ad-format') || ''));
        }
        function scan(node) {
            if (!node || node.nodeType !== 1) return;
            try {
                if (node.matches && node.matches('ins.adsbygoogle')) inspect(node);
                if (node.querySelectorAll) {
                    var found = node.querySelectorAll('ins.adsbygoogle');
                    for (var i = 0; i < found.length; i++) inspect(found[i]);
                }
            } catch (e) {}
        }
        try {
            new MutationObserver(function (muts) {
                for (var i = 0; i < muts.length; i++) {
                    var added = muts[i].addedNodes;
                    for (var j = 0; j < added.length; j++) scan(added[j]);
                }
            }).observe(document.documentElement, { childList: true, subtree: true });
        } catch (e) {}
        window.addEventListener('hashchange', function () {
            if (/google_vignette/i.test(location.hash || '')) report('vignette', location.hash);
        });
    }

    function initAllowed() {
        var slots = document.querySelectorAll('.tool-ad-slot[data-ad-slot-provider]');
        if (!slots.length) return;

        // Admin / paid no-ads: remove the placeholders entirely.
        //
        // We can't gate <x-tool-ad-slot> server-side — the blade is
        // compiled into PageCache and branching on session() there
        // poisons the cache (see feedback_pagecache_session_branch.md
        // 2026-04-26 incident on /ru/extract-audio). So the server
        // always emits a `.tool-ad-slot` placeholder with the
        // 280-px reservation; this function removes it on clients
        // that should not see ads. .remove() rather than
        // .classList.add('is-failed') because for the admin path the
        // ad NEVER tried to load — using the "load-failed" class
        // would be semantically wrong, and any future CSS targeting
        // .tool-ad-slot would still match a hidden element.
        // Originally surfaced as a 280-px gap between howto and the
        // H2 subtitle on /audio-to-notes for an admin viewer.
        if (shouldHideAds()) {
            slots.forEach(function (slotEl) { slotEl.remove(); });
            return;
        }

        watchAutoAds();
        slots.forEach(maybeLazyHydrate);
    }

    // Focus-mode API — tools with a distraction-free running phase
    // (hearing-test, dementia-test, iq-test) call this when the phase
    // starts. Their focus CSS hides ad containers with display:none, but a
    // slot that has NOT yet rendered would otherwise still fill while
    // hidden — the served-but-not-viewed ghost-impression pattern AdSense
    // flags as invalid traffic (same class as the 2026-05-29 fix #1).
    // Pending surfaces are removed from the DOM so no late fill can land;
    // already-rendered ads stay (they were viewable before the phase began,
    // the focus CSS merely hides them for the duration).
    window.__toolAdSlots = window.__toolAdSlots || {};
    window.__toolAdSlots.removePending = function () {
        document.querySelectorAll('.tool-ad-slot:not(.is-loaded)').forEach(function (el) { el.remove(); });
        var as = document.getElementById('asStickyWrap');
        if (as && !as.classList.contains('as-ready')) as.remove();
        var ya = document.getElementById('rsyaStickyWrap');
        if (ya && !ya.classList.contains('rsya-ready')) ya.remove();
        // The ru mobile floor bar (layouts/app.blade.php) has no wrap of ours —
        // Yandex injects and owns its fixed container, so there is no node to
        // delete. Flag it instead: the floor's renderAd() refuses to fire while
        // this is set, which is the same "no late fill into a hidden surface"
        // guarantee the .remove() calls above give the other placements.
        window.__toolAdSlots.__floorSuppressed = true;
    };

    function boot() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    }

    // Пререндер Chrome выполняет скрипты страницы ДО того, как её
    // открыл человек, и страница может быть не открыта вовсе. Гидратация
    // здесь запрашивала бы объявление — и засчитывала показ — для никем не
    // увиденной страницы. Для рекламной сети это невалидный трафик, а для
    // нас — завышенный ad_impression, по которому потом считают fill rate.
    //
    // Повод не умозрительный: sidebar.js с некоторых пор отдаёт правила
    // Speculation Rules с prerender/moderate, и браузер их ПРИНИМАЕТ — проверено
    // 24.08 через CDP-домен Preload на живом проде: ruleSetUpdated без
    // errorType, 55 зарегистрированных источников предзагрузки.
    //
    // ⚠️ Сам факт пререндера в Playwright ПРОВЕРИТЬ НЕЛЬЗЯ: присоединённый
    // DevTools его выключает (prerenderStatus: PrerenderingDisabledByDevTools),
    // а подключён он в Playwright всегда. Поэтому «пререндера не
    // было» в любом нашем тесте — свойство стенда, не сайта.
    if (document.prerendering) {
        document.addEventListener('prerenderingchange', boot, { once: true });
    } else {
        boot();
    }
})();
